import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Bell, ArrowRight, Trash2, Mail, Settings, LogOut, Globe, Activity } from 'lucide-react';
import PageLayout from '@/components/layout/PageLayout';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MyTrackerPage() {
  const [searchParams] = useSearchParams();
  const urlToken = searchParams.get('token');
  const [token, setToken] = useState(urlToken || sessionStorage.getItem('tracker_token') || '');
  const [subscriber, setSubscriber] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [email, setEmail] = useState('');
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Store token from URL
  useEffect(() => {
    if (urlToken) sessionStorage.setItem('tracker_token', urlToken);
  }, [urlToken]);

  // Load subscriber data
  useEffect(() => {
    if (!token) { setLoading(false); return; }
    fetch(`${API}/api/tracker/me?token=${token}`)
      .then(r => { if (!r.ok) throw new Error('Invalid token'); return r.json(); })
      .then(d => { setSubscriber(d); setLoading(false); })
      .catch(() => { setLoading(false); setError('expired'); sessionStorage.removeItem('tracker_token'); setToken(''); });
  }, [token]);

  const handleMagicLink = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    try {
      const res = await fetch(`${API}/api/tracker/magic-link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim().toLowerCase() })
      });
      if (res.ok) setMagicLinkSent(true);
      else setError('not_found');
    } catch { setError('network'); }
  };

  const handleUntrack = async (item) => {
    await fetch(`${API}/api/tracker/untrack`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token, country_code: item.country_code, category: item.category })
    });
    setSubscriber(prev => ({
      ...prev,
      tracked_items: prev.tracked_items.filter(t => !(t.country_code === item.country_code && t.category === item.category))
    }));
  };

  const handleDelete = async () => {
    await fetch(`${API}/api/tracker/delete-account`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token })
    });
    sessionStorage.removeItem('tracker_token');
    setSubscriber(null);
    setToken('');
    setDeleteConfirm(false);
  };

  const handleUnsubscribe = async () => {
    await fetch(`${API}/api/tracker/unsubscribe?token=${token}`, { method: 'POST' });
    setSubscriber(prev => ({ ...prev, email_preferences: { ...prev.email_preferences, enabled: false } }));
  };

  if (loading) return <PageLayout><div className="min-h-screen flex items-center justify-center text-slate-400">Loading your tracker...</div></PageLayout>;

  // Not logged in — request magic link
  if (!subscriber) {
    return (
      <PageLayout>
        <div className="min-h-screen flex items-center justify-center bg-slate-50" data-testid="my-tracker-login">
          <div className="max-w-sm w-full mx-auto px-6">
            <div className="bg-white rounded-2xl border border-slate-200 shadow-lg p-8 text-center">
              <Globe className="w-10 h-10 text-taxera-blue mx-auto mb-4" />
              <h1 className="font-heading text-xl font-bold text-taxera-charcoal mb-2">Access My Tracker</h1>
              <p className="text-sm text-slate-500 mb-6">Enter your email to receive a personal access link. No password needed.</p>

              {magicLinkSent ? (
                <div className="bg-green-50 rounded-xl p-4">
                  <Mail className="w-6 h-6 text-green-500 mx-auto mb-2" />
                  <p className="text-sm font-semibold text-green-700">Check your email</p>
                  <p className="text-xs text-green-600 mt-1">We've sent you an access link.</p>
                </div>
              ) : (
                <form onSubmit={handleMagicLink} className="space-y-3">
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Your email address"
                    required
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:border-taxera-teal"
                    data-testid="magic-link-email"
                  />
                  {error === 'not_found' && <p className="text-xs text-red-500">Email not found. Start by tracking a country first.</p>}
                  {error === 'expired' && <p className="text-xs text-amber-600">Your session expired. Request a new access link.</p>}
                  <button type="submit" className="w-full py-2.5 rounded-xl bg-taxera-blue text-white text-sm font-bold hover:bg-taxera-blue/90 transition-colors" data-testid="magic-link-btn">
                    Send access link
                  </button>
                </form>
              )}

              <p className="text-xs text-slate-400 mt-4">
                Don't have a tracker yet? <Link to="/tracker" className="text-taxera-blue font-semibold hover:underline">Start exploring</Link>
              </p>
            </div>
          </div>
        </div>
      </PageLayout>
    );
  }

  // Logged in — show dashboard
  return (
    <PageLayout>
      <div className="min-h-screen bg-slate-50" data-testid="my-tracker-dashboard">
        <div className="max-w-3xl mx-auto px-6 py-12">

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="font-heading text-2xl font-bold text-taxera-charcoal">
                {subscriber.first_name ? `${subscriber.first_name}'s Tracker` : 'My Tracker'}
              </h1>
              <p className="text-sm text-slate-500 mt-0.5">
                Tracking {subscriber.tracked_items?.length || 0} {subscriber.tracked_items?.length === 1 ? 'country' : 'countries'}
              </p>
            </div>
            <Link to="/tracker" className="text-xs font-bold text-taxera-teal hover:underline flex items-center gap-1">
              Explore more <ArrowRight className="w-3 h-3" />
            </Link>
          </div>

          {/* Tracked items */}
          {subscriber.tracked_items?.length > 0 ? (
            <div className="space-y-3 mb-10">
              {subscriber.tracked_items.map((item, i) => (
                <motion.div
                  key={`${item.country_code}-${item.category}`}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="bg-white rounded-xl border border-slate-200 p-4 flex items-center justify-between group"
                >
                  <Link
                    to={`/tracker/${item.category}/${item.country_code.toLowerCase()}`}
                    className="flex items-center gap-3 flex-1"
                  >
                    <img
                      src={`https://flagcdn.com/32x24/${item.country_code.toLowerCase()}.png`}
                      alt={item.country_code}
                      className="w-8 h-6 rounded-sm object-cover border border-slate-200"
                    />
                    <div>
                      <span className="text-sm font-bold text-taxera-charcoal group-hover:text-taxera-blue transition-colors">{item.country_code}</span>
                      <span className="text-xs text-slate-400 ml-2 capitalize">{item.category.replace('_', ' ')}</span>
                    </div>
                  </Link>
                  <button
                    onClick={() => handleUntrack(item)}
                    className="p-2 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100"
                    data-testid={`untrack-${item.country_code.toLowerCase()}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center mb-10">
              <Globe className="w-8 h-8 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500 mb-3">You're not tracking any countries yet.</p>
              <Link to="/tracker/einvoicing" className="text-sm font-bold text-taxera-blue hover:underline">
                Start tracking
              </Link>
            </div>
          )}

          {/* Settings */}
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="tracker-settings">
            <h3 className="text-sm font-bold text-taxera-charcoal mb-4 flex items-center gap-2">
              <Settings className="w-4 h-4 text-slate-400" /> Settings
            </h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between py-2 border-b border-slate-100">
                <div>
                  <span className="text-sm text-slate-600">Email notifications</span>
                  <p className="text-xs text-slate-400">{subscriber.email}</p>
                </div>
                {subscriber.email_preferences?.enabled ? (
                  <button onClick={handleUnsubscribe} className="text-xs font-semibold text-slate-500 hover:text-red-500 transition-colors" data-testid="unsubscribe-btn">
                    Turn off
                  </button>
                ) : (
                  <span className="text-xs text-slate-400">Disabled</span>
                )}
              </div>
              <div className="flex items-center justify-between py-2">
                <span className="text-sm text-red-500">Delete account & all data</span>
                {deleteConfirm ? (
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-red-500">Are you sure?</span>
                    <button onClick={handleDelete} className="text-xs font-bold text-red-600 bg-red-50 px-3 py-1 rounded-full hover:bg-red-100 transition-colors" data-testid="confirm-delete-btn">
                      Yes, delete everything
                    </button>
                    <button onClick={() => setDeleteConfirm(false)} className="text-xs text-slate-400 hover:text-slate-600">
                      Cancel
                    </button>
                  </div>
                ) : (
                  <button onClick={() => setDeleteConfirm(true)} className="text-xs font-semibold text-red-500 hover:underline" data-testid="delete-account-btn">
                    Delete
                  </button>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </PageLayout>
  );
}
